import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Box, Button, CopyButton, Group, Modal, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconCopy, IconDownload, IconTrash } from '@tabler/icons-react';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import { svgToPngDataUrl, updateRobotConfigs } from '@mygpt/app';
import type { ShareParamLink } from '@mygpt/app';

import { useGPTStore } from '@/store';

interface ShareParamLinkModalProps {
  opened: boolean;
  onClose: () => void;
  botId: string;
  /** 不带参数的分享链接 */
  shareLink: string;
  /** 无编辑权限时只能查看和下载 */
  readOnly?: boolean;
}

// 参数名只允许 URL 安全字符，避免生成的链接被客户误改或无法解析
const PARAM_KEY_PATTERN = /^[A-Za-z0-9_-]{1,50}$/;
const PARAM_VALUE_MAX_LENGTH = 100;
const MAX_LINKS = 100;
// 导出到 Excel 时的二维码尺寸（像素），兼顾清晰度与表格可读性
const EXPORT_QR_SIZE = 140;

/** 兼容后端返回 JSON 字符串或数组两种形式 */
const parseShareParamLinks = (raw: unknown): ShareParamLink[] => {
  let list: unknown = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch (e) {
      console.error('分享参数配置解析失败', e);
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.filter(
    (item): item is ShareParamLink => typeof item?.key === 'string' && typeof item?.value === 'string'
  );
};

/** 在分享链接上追加参数，已有同名参数时覆盖 */
const buildParamUrl = (baseUrl: string, { key, value }: ShareParamLink) => {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set(key, value);
    return url.toString();
  } catch (e) {
    // 基础链接异常（如环境变量缺失）时退化为字符串拼接，避免弹窗白屏
    console.error('分享链接解析失败', baseUrl, e);
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }
};

/** 导出文件名：「标题_bot 名称」，替换系统不允许的文件名字符 */
const buildExportFileName = (title: string, botName: string) =>
  `${title}_${botName}`
    .replace(/[\\/:*?"<>|]/g, '_')
    .trim()
    .slice(0, 100);

const ShareParamLinkModal = ({ opened, onClose, botId, shareLink, readOnly = false }: ShareParamLinkModalProps) => {
  const { t } = useTranslation();
  const { robotInfo, setRobotInfo } = useGPTStore();
  const [links, setLinks] = useState<ShareParamLink[]>([]);
  const [paramKey, setParamKey] = useState('');
  const [paramValue, setParamValue] = useState('');
  const [keyError, setKeyError] = useState('');
  const [valueError, setValueError] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // store 中的 robotInfo 只有与当前 bot 一致时才可信
  const isCurrentBot = robotInfo?.id === botId;
  const savedLinks = isCurrentBot ? robotInfo?.configs?.share_param_links : undefined;

  useEffect(() => {
    if (opened) {
      setLinks(parseShareParamLinks(savedLinks));
    }
  }, [opened, savedLinks]);

  const rows = useMemo(
    () =>
      links.map((link, index) => ({ ...link, url: buildParamUrl(shareLink, link), qrId: `share-param-qr-${index}` })),
    [links, shareLink]
  );

  const resetInputs = () => {
    setParamKey('');
    setParamValue('');
    setKeyError('');
    setValueError('');
  };

  const handleClose = () => {
    resetInputs();
    onClose();
  };

  // 保存到 bot configs，失败时回滚
  const saveLinks = async (next: ShareParamLink[]) => {
    const prev = links;
    setLinks(next);
    setSaving(true);
    try {
      await updateRobotConfigs({ aiId: botId, configs: { share_param_links: next } });
      if (isCurrentBot && robotInfo) {
        setRobotInfo({ ...robotInfo, configs: { ...robotInfo.configs, share_param_links: next } });
      }
      return true;
    } catch (e) {
      console.error('分享参数配置保存失败', e);
      setLinks(prev);
      showNotification({ message: t('shareSetting.saveFailed'), color: 'red' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    const key = paramKey.trim();
    const value = paramValue.trim();
    const nextKeyError = PARAM_KEY_PATTERN.test(key) ? '' : t('shareParamLink.key_invalid');
    const nextValueError = value ? '' : t('shareParamLink.value_required');
    setKeyError(nextKeyError);
    setValueError(nextValueError);
    if (nextKeyError || nextValueError) return;

    if (links.some((link) => link.key === key && link.value === value)) {
      setValueError(t('shareParamLink.duplicated'));
      return;
    }
    if (links.length >= MAX_LINKS) {
      showNotification({ message: t('shareParamLink.limit_reached', { count: MAX_LINKS }), color: 'red' });
      return;
    }

    const saved = await saveLinks([...links, { key, value }]);
    if (saved) resetInputs();
  };

  const handleDelete = (index: number) => {
    saveLinks(links.filter((_, i) => i !== index));
  };

  // 导出 Excel：参数名 / 参数值 / URL / 二维码图片
  const handleExport = async () => {
    setExporting(true);
    try {
      const { default: ExcelJS } = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('QR');
      sheet.columns = [
        { header: t('shareParamLink.key_label'), key: 'key', width: 20 },
        { header: t('shareParamLink.value_label'), key: 'value', width: 24 },
        { header: t('shareParamLink.col_url'), key: 'url', width: 70 },
        { header: t('shareParamLink.col_qrcode'), key: 'qr', width: 22 },
      ];
      sheet.getRow(1).font = { bold: true };

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const excelRow = sheet.addRow({ key: row.key, value: row.value, url: { text: row.url, hyperlink: row.url } });
        // 行高单位为磅，约等于像素 * 0.75
        excelRow.height = EXPORT_QR_SIZE * 0.75 + 12;
        excelRow.alignment = { vertical: 'middle', wrapText: true };

        const pngUrl = await svgToPngDataUrl(`#${row.qrId}`, 8);
        if (pngUrl) {
          const imageId = workbook.addImage({ base64: pngUrl, extension: 'png' });
          sheet.addImage(imageId, {
            tl: { col: 3, row: i + 1 },
            ext: { width: EXPORT_QR_SIZE, height: EXPORT_QR_SIZE },
          });
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${buildExportFileName(
        t('shareParamLink.title'),
        (isCurrentBot && robotInfo?.name) || botId
      )}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('分享参数 Excel 导出失败', e);
      showNotification({ message: t('shareParamLink.export_failed'), color: 'red' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Text fw={600}>{t('shareParamLink.title')}</Text>}
      centered
      size={640}
      radius={16}
    >
      <Stack gap={16}>
        <Text size="sm" c="gray.6">
          {t('shareParamLink.description')}
        </Text>

        {!readOnly && (
          <Group align="flex-start" gap={8} wrap="nowrap">
            <TextInput
              className="flex-1"
              label={t('shareParamLink.key_label')}
              placeholder={t('shareParamLink.key_placeholder')}
              value={paramKey}
              error={keyError}
              maxLength={50}
              onChange={(e) => {
                setParamKey(e.currentTarget.value);
                setKeyError('');
              }}
            />
            <TextInput
              className="flex-1"
              label={t('shareParamLink.value_label')}
              placeholder={t('shareParamLink.value_placeholder')}
              value={paramValue}
              error={valueError}
              maxLength={PARAM_VALUE_MAX_LENGTH}
              onChange={(e) => {
                setParamValue(e.currentTarget.value);
                setValueError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleGenerate();
              }}
            />
            <Button mt={25} color="violet" loading={saving} onClick={handleGenerate}>
              {t('shareParamLink.generate')}
            </Button>
          </Group>
        )}

        {rows.length === 0 ? (
          <Text size="sm" c="gray.5" ta="center" py={24}>
            {t('shareParamLink.empty')}
          </Text>
        ) : (
          <Stack gap={8} mah={360} style={{ overflowY: 'auto' }}>
            {rows.map((row, index) => (
              <Group
                key={`${row.key}=${row.value}`}
                gap={12}
                wrap="nowrap"
                p={8}
                style={{ border: '1px solid #E5E7EB', borderRadius: 8 }}
              >
                <QRCode id={row.qrId} value={row.url} size={72} bgColor="#ffffff" fgColor="#000000" />
                <Box className="flex-1 min-w-0">
                  <Text size="sm" fw={600}>
                    {row.key} = {row.value}
                  </Text>
                  <Text size="xs" c="gray.6" truncate="end">
                    {row.url}
                  </Text>
                </Box>
                <CopyButton value={row.url} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip label={t('copy_link')}>
                      <ActionIcon
                        variant="subtle"
                        color={copied ? 'teal' : 'gray'}
                        onClick={() => {
                          copy();
                          showNotification({ message: t('link_copied'), color: 'green.5' });
                        }}
                      >
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
                {!readOnly && (
                  <Tooltip label={t('shareParamLink.delete')}>
                    <ActionIcon variant="subtle" color="red" disabled={saving} onClick={() => handleDelete(index)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ))}
          </Stack>
        )}

        <Group justify="flex-end">
          <Button
            variant="light"
            color="violet"
            leftSection={<IconDownload size={16} />}
            disabled={rows.length === 0}
            loading={exporting}
            onClick={handleExport}
          >
            {t('shareParamLink.download_excel')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ShareParamLinkModal;
